import React, { useState, useMemo, useEffect } from 'react';
import { Save, X, Plus, Trash } from 'lucide-react';
import { api } from '../../services/api';
import { parseCustodyVehicleId } from '../../utils/custodyVehicle';

export const ReturnForm = ({ assignments, materials, people = [], warehouses = [], onSave, onCancel }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [formData, setFormData] = useState({
    assignmentId: '',
    responsibleDoc: '',
    returnQuantity: 0,
    materialState: 'Bueno',
    observations: ''
  });

  const [items, setItems] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null);
  const [extraByDoc, setExtraByDoc] = useState({});
  const [extraAssignments, setExtraAssignments] = useState([]);

  const systemUsersMap = useMemo(() => {
    try {
      const raw = localStorage.getItem('senerfi_user_prefs');
      const data = raw ? JSON.parse(raw) : {};
      const users = Array.isArray(data.users) ? data.users : [];
      return new Map(users.map(u => [
        String(u.responsibleCedula || u.responsible_cedula || u.cedula || '').trim(), 
        u.name || u.nombre || ''
      ]).filter(([doc]) => doc));
    } catch { return new Map(); }
  }, []);

  const mergedAssignments = useMemo(() => {
    const byId = new Map();
    // Primero cargar locales
    (assignments || []).forEach(a => {
      const k = String(a.id || `${a.materialId}-${a.responsibleDoc}-${a.warehouseId}-${a.assignDate}-${a.serial}`);
      byId.set(k, a);
    });
    // Luego sobrescribir con las de backend (traen serial/marca completos)
    (extraAssignments || []).forEach(a => {
      const k = String(a.id || `${a.materialId}-${a.responsibleDoc}-${a.warehouseId}-${a.assignDate}-${a.serial}`);
      byId.set(k, a);
    });
    return Array.from(byId.values());
  }, [assignments, extraAssignments]);

  const requiresSerialById = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => {
      const mid = Number(m?.id);
      if (!mid) return;
      const req = !!m?.requiresSerial || !!String(m?.serial || '').trim();
      map.set(mid, req);
    });
    return map;
  }, [materials]);

  const normalizeWarehouseIdForFilter = (v) => {
    const s = String(v || '').toLowerCase().trim();
    const noAcc = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const slug = noAcc.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (/^popayan/.test(slug)) return 'popayan';
    if (/^santander/.test(slug) || /quilichao/.test(slug)) return 'santander';
    if (/^(c054|lv).*centro/.test(slug)) return 'lv-centro';
    if (/^(c086|lv).*norte/.test(slug)) return 'lv-norte';
    if (/^ap-emcali-norte/.test(slug)) return 'ap-emcali-norte';
    if (/^ap-emcali-material/.test(slug)) return 'ap-emcali-material';
    return slug || s;
  };

  const activeAssignments = mergedAssignments.filter(a =>
    a.status === 'Asignado'
    && (!selectedWarehouse || normalizeWarehouseIdForFilter(a.warehouseId) === normalizeWarehouseIdForFilter(selectedWarehouse))
    && (!requiresSerialById.get(Number(a.materialId)) || String(a.serial || '').trim())
  );

  const resolveDocToDni = useMemo(() => {
    const peopleArr = Array.isArray(people) ? people : [];
    const docOf = (p) => String(p?.dni || p?.cedula || p?.documento_identidad || p?.documento || p?.doc || p?.id || '').trim();
    const dniSet = new Set(peopleArr.map((p) => docOf(p)).filter(Boolean));
    const byDbId = new Map(peopleArr.map((p) => [String(p?._dbId ?? '').trim(), docOf(p)]).filter(([k, v]) => k && v));
    const byId = new Map(peopleArr.map((p) => [String(p?.id ?? '').trim(), docOf(p)]).filter(([k, v]) => k && v));
    const byDoc = new Map();
    peopleArr.forEach((p) => {
      const v = docOf(p);
      if (!v) return;
      [p?.dni, p?.cedula, p?.documento_identidad, p?.documento, p?.doc].forEach((k) => {
        const kk = String(k || '').trim();
        if (kk) byDoc.set(kk, v);
      });
    });
    return (doc) => {
      const raw = String(doc || '').trim();
      if (!raw) return '';
      const digits = raw.replace(/[^0-9]/g, '');
      if (dniSet.has(raw)) return raw;
      if (digits && dniSet.has(digits)) return digits;
      const m1 = byDbId.get(raw) || (digits ? byDbId.get(digits) : null);
      if (m1) return m1;
      const m2 = byId.get(raw) || (digits ? byId.get(digits) : null);
      if (m2) return m2;
      const m3 = byDoc.get(raw) || (digits ? byDoc.get(digits) : null);
      if (m3) return m3;
      return digits || raw;
    };
  }, [people]);

  // Responsables únicos con asignaciones activas
  const responsibles = useMemo(() => {
    const byId = new Map();

    (people || []).forEach((p) => {
      const id = String(p?.dni || p?.cedula || p?.documento_identidad || p?.documento || p?.doc || p?.id || '').trim();
      if (!id) return;
      const name = String(p.name || p.nombre || p.nombre_completo || '').trim() || id;
      byId.set(id, name);
    });

    const docs = Array.from(new Set(activeAssignments.map(a => String(resolveDocToDni(a.responsibleDoc || '')).trim()).filter(Boolean)));
    const byDocAssign = new Map(activeAssignments.map(a => [String(resolveDocToDni(a.responsibleDoc || '')).trim(), String(a.responsibleName || '')]));

    docs.forEach((doc) => {
      if (byId.has(doc)) return;
      let name = systemUsersMap.get(doc) || (extraByDoc[doc]?.name || '');
      if (!name) {
        const assignName = byDocAssign.get(doc);
        if (assignName && assignName !== doc && !/^\d+$/.test(assignName)) {
          name = assignName;
        }
      }
      byId.set(doc, name || doc);
    });

    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [activeAssignments, people, extraByDoc, systemUsersMap, resolveDocToDni]);

  // Asignaciones del responsable seleccionado
  const personAssignments = useMemo(() => {
    if (!formData.responsibleDoc) return [];
    const wanted = resolveDocToDni(formData.responsibleDoc);
    const filtered = activeAssignments.filter(a => resolveDocToDni(a.responsibleDoc) === wanted);

    const score = (a) => {
      let s = 0;
      if (a?.assignmentDbId) s += 4;
      if (String(a?.serial || '').trim()) s += 2;
      if (String(a?.brandName || '').trim()) s += 1;
      if (String(a?.materialCode || '').trim()) s += 1;
      return s;
    };

    const bestByKey = new Map();
    filtered.forEach((a) => {
      const mid = Number(a?.materialId);
      const serial = String(a?.serial || '').trim();
      const key = serial
        ? `ser:${Number.isFinite(mid) && mid > 0 ? `mid:${mid}` : `code:${String(a?.materialCode || '').trim()}`}|${serial}`
        : `id:${String(a?.assignmentDbId ?? a?.id ?? '').trim()}`;
      const prev = bestByKey.get(key);
      if (!prev) { bestByKey.set(key, a); return; }
      const ps = score(prev);
      const ns = score(a);
      if (ns > ps) { bestByKey.set(key, a); return; }
      if (ns === ps) {
        const pdt = String(prev?.assignDate || '').slice(0, 10);
        const ndt = String(a?.assignDate || '').slice(0, 10);
        if (ndt && (!pdt || ndt > pdt)) bestByKey.set(key, a);
      }
    });

    return Array.from(bestByKey.values()).sort((a, b) => {
      const an = String(a?.materialName || '').localeCompare(String(b?.materialName || ''), 'es', { sensitivity: 'base' });
      if (an !== 0) return an;
      const as = String(a?.serial || '').localeCompare(String(b?.serial || ''), 'es', { sensitivity: 'base' });
      if (as !== 0) return as;
      const ab = String(a?.brandName || '').localeCompare(String(b?.brandName || ''), 'es', { sensitivity: 'base' });
      if (ab !== 0) return ab;
      return String(a?.id || '').localeCompare(String(b?.id || ''), 'es', { sensitivity: 'base' });
    });
  }, [activeAssignments, formData.responsibleDoc, resolveDocToDni]);

  const selectedAssignment = useMemo(() => {
    if (!formData.assignmentId) return null;
    return personAssignments.find(a => String(a.id) === String(formData.assignmentId)) || null;
  }, [personAssignments, formData.assignmentId]);

  const materialById = useMemo(() => {
    const map = new Map();
    materials.forEach(m => map.set(m.id, m));
    return map;
  }, [materials]);

  const selectedMaterial = useMemo(() => {
    if (!selectedAssignment) return null;
    return materialById.get(selectedAssignment.materialId) || null;
  }, [materialById, selectedAssignment]);

  const selectedResponsibleName = useMemo(() => {
    const doc = String(formData.responsibleDoc || selectedAssignment?.responsibleDoc || '').trim();
    if (!doc) return '';
    const docOf = (p) => String(p?.dni || p?.cedula || p?.documento_identidad || p?.documento || p?.doc || p?.id || '').trim();
    const byDoc = new Map((people || []).map((p) => [docOf(p), String(p.name || p.nombre || p.nombre_completo || '').trim()]).filter(([k]) => k));
    const d = resolveDocToDni(doc);
    let name = byDoc.get(d) || systemUsersMap.get(d) || (extraByDoc[d]?.name || '');
    if (!name) {
      const assignName = selectedAssignment?.responsibleName;
      if (assignName && assignName !== d && !/^\d+$/.test(assignName)) {
        name = assignName;
      }
    }
    return name || '';
  }, [formData.responsibleDoc, selectedAssignment, people, extraByDoc, systemUsersMap, resolveDocToDni]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const docs = Array.from(new Set(activeAssignments.map(a => String(resolveDocToDni(a.responsibleDoc || '')).trim()).filter(Boolean)));
        const docOf = (p) => String(p?.dni || p?.cedula || p?.documento_identidad || p?.documento || p?.doc || p?.id || '').trim();
        const byDoc = new Map((people || []).map((p) => [docOf(p), String(p.name || p.nombre || p.nombre_completo || '').trim()]).filter(([k]) => k));
        const missing = docs.filter(d => {
          const nm = byDoc.get(d) || systemUsersMap.get(d);
          return !nm || nm.trim() === '' || nm === d;
        });
        if (!missing.length) return;
        const fetched = {};
        await Promise.all(missing.map(async (dni) => {
          try {
            const r = await api.people.get({ dni, limit: 1 });
            const dataRaw = Array.isArray(r?.data) ? r.data[0] : (Array.isArray(r) ? r[0] : (r?.data || null));
            if (dataRaw) fetched[dni] = { name: dataRaw.name || dataRaw.nombre || '', dni };
          } catch (_) {}
        }));
        if (!cancelled && Object.keys(fetched).length) {
          setExtraByDoc(prev => ({ ...prev, ...fetched }));
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [activeAssignments, people, systemUsersMap, resolveDocToDni]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!formData.responsibleDoc) { setExtraAssignments([]); return; }
      const dni = resolveDocToDni(formData.responsibleDoc);
      let acc = [];
      try {
        const page = 1000;
        for (let off = 0; ; off += page) {
          const resp = await api.assignments.get({ dni, limit: page, offset: off, order: 'desc' });
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          if (rows.length === 0) break;
          acc = acc.concat(rows.map((a) => ({
            id: a.id,
            assignmentDbId: a.id,
            materialId: a.material_id,
            materialName: a.material_name || '',
            materialCode: a.material_code || '',
            materialCategory: a.material_category || a.materialCategory || '',
            serial: String(a.serial || '').trim(),
            brandName: String(a.brand || '').trim(),
            responsibleDoc: resolveDocToDni(a.person_dni || a.person_id),
            responsibleName: a.person_name || String(a.person_dni || a.person_id || ''),
            quantity: a.quantity,
            assignDate: a.created_at ? String(a.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
            hasPeripherals: String(a.serial || '').trim() ? 'Sí' : 'No',
            status: 'Asignado',
            type: 'Asignación',
            observations: a.notes || '',
            custodyVehicleId: parseCustodyVehicleId(a.notes),
            evidencePhotos: [],
            warehouseId: a.warehouse_id || ''
          })));
          if (rows.length < page) break;
        }
      } catch (_) {}

      try {
        const normalizeMovType = (v) =>
          String(v || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();

        const formatYmd = (d) => {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };

        const now = new Date();
        const oneYearAgo = new Date(now.getTime());
        oneYearAgo.setDate(oneYearAgo.getDate() - 365);
        const dates = Array.from(new Set(acc.map((a) => String(a.assignDate || '').slice(0, 10)).filter(Boolean))).sort();
        const start_date = dates.length ? dates[0] : formatYmd(oneYearAgo);
        const end_date = dates.length ? dates[dates.length - 1] : formatYmd(now);

        const pageMv = 1000;
        const maxPages = 50;
        const lastBySerialKey = new Map();
        for (let p = 0; p < maxPages; p += 1) {
          const off = p * pageMv;
          const mresp = await api.movements.get({ dni, start_date, end_date, order: 'desc', limit: pageMv, offset: off });
          const mrows = Array.isArray(mresp?.data) ? mresp.data : (mresp?.data ? [mresp.data] : []);
          if (mrows.length === 0) break;
          mrows.forEach((mv) => {
            const t = normalizeMovType(mv?.type || '');
            if (!['asignacion', 'prestamo', 'devolucion', 'salida', 'consumible'].includes(t)) return;
            const mid = Number(mv?.material_id);
            const wid = normalizeWarehouseIdForFilter(mv?.warehouse_id || '');
            const serial = String(mv?.serial || '').trim();
            if (!mid || !wid || !serial) return;
            const key = `${mid}|${wid}|${serial}`;
            if (!lastBySerialKey.has(key)) lastBySerialKey.set(key, mv);
          });
          if (mrows.length < pageMv) break;
        }

        const materialMap = new Map((materials || []).map((m) => [Number(m?.id), m]));
        const existingSerialKeys = new Set(acc.map((a) => `${Number(a.materialId)}|${normalizeWarehouseIdForFilter(a.warehouseId)}|${String(a.serial || '').trim()}`));
        const serialItems = [];
        Array.from(lastBySerialKey.entries()).forEach(([key, mv]) => {
          const t = normalizeMovType(mv?.type || '');
          if (t !== 'asignacion' && t !== 'prestamo') return;
          const mid = Number(mv?.material_id);
          const wid = normalizeWarehouseIdForFilter(mv?.warehouse_id || '');
          const serial = String(mv?.serial || '').trim();
          const sk = `${mid}|${wid}|${serial}`;
          if (existingSerialKeys.has(sk)) return;
          existingSerialKeys.add(sk);
          const mat = materialMap.get(mid);
          serialItems.push({
            id: `mv-${mv?.id || key}`,
            assignmentDbId: null,
            materialId: mid,
            materialName: String(mv?.material_name || mat?.name || '').trim(),
            materialCode: String(mv?.material_code || mat?.code || '').trim(),
            materialCategory: String(mv?.material_category || mat?.category || mat?.type || '').trim(),
            serial,
            brandName: String(mv?.brand || '').trim(),
            responsibleDoc: dni,
            responsibleName: String(mv?.requester_name || '').trim(),
            quantity: 1,
            assignDate: String(mv?.created_at || '').slice(0, 10) || '',
            hasPeripherals: 'Sí',
            status: 'Asignado',
            type: t === 'prestamo' ? 'Préstamo' : 'Asignación',
            observations: String(mv?.notes || '').trim(),
            evidencePhotos: [],
            warehouseId: String(mv?.warehouse_id || '').trim()
          });
        });

        if (serialItems.length > 0) {
          const serialMaterialIds = new Set(serialItems.map((x) => Number(x.materialId)));
          acc = acc.filter((a) => !requiresSerialById.get(Number(a.materialId)) || !serialMaterialIds.has(Number(a.materialId)));
          acc = acc.concat(serialItems);
        }
      } catch (_) {}

      try {
        const personRow = (people || []).find((p) => resolveDocToDni(p?.dni || p?.id) === dni);
        const personDbId = Number(personRow?._dbId || personRow?.id || 0);
        if (personDbId > 0) {
          const vRes = await api.vehicleAssignments.get();
          const vehList = Array.isArray(vRes?.data) ? vRes.data : [];
          const dotItems = [];
          for (const v of vehList) {
            const vid = Number(v?.id || 0);
            if (!vid) continue;
            const activeForPerson = (v.current_assignments || []).filter(
              (va) => String(va?.status || '') === 'Activo' && Number(va?.person_id || 0) === personDbId
            );
            if (!activeForPerson.length) continue;
            const dotRes = await api.vehicleDotacion.get({ vehicle_id: vid });
            const dotRows = Array.isArray(dotRes?.data) ? dotRes.data : [];
            dotRows.forEach((dr, idx) => {
              const mid = Number(dr?.material_id || 0);
              const wid = String(dr?.warehouse_id || '').trim();
              const serial = String(dr?.serial || '').trim();
              const dupKey = `${mid}|${normalizeWarehouseIdForFilter(wid)}|${serial}`;
              const exists = acc.some((a) => {
                const ak = `${Number(a.materialId)}|${normalizeWarehouseIdForFilter(a.warehouseId)}|${String(a.serial || '').trim()}`;
                return ak === dupKey;
              });
              if (exists) return;
              dotItems.push({
                id: `veh-dot-${vid}-${idx}-${mid}`,
                assignmentDbId: null,
                materialId: mid,
                materialName: dr.material_name || dr.material_code || '',
                materialCode: dr.material_code || '',
                materialCategory: dr.dotacion_category || '',
                serial,
                brandName: String(dr.brand || '').trim(),
                responsibleDoc: dni,
                responsibleName: personRow?.name || dni,
                quantity: Number(dr.quantity) || 1,
                assignDate: dr.assigned_at ? String(dr.assigned_at).slice(0, 10) : '',
                hasPeripherals: serial ? 'Sí' : 'No',
                status: 'Asignado',
                type: 'Asignación',
                observations: `Dotación vehículo ${v.plate || vid}`,
                custodyVehicleId: vid,
                evidencePhotos: [],
                warehouseId: wid,
              });
            });
          }
          if (dotItems.length) acc = acc.concat(dotItems);
        }
      } catch (_) {}

      if (!cancelled) setExtraAssignments(acc);
    })();
    return () => { cancelled = true; };
  }, [formData.responsibleDoc, resolveDocToDni, materials, requiresSerialById, people]);

  const canAdd = useMemo(() => {
    const qtyNum = parseInt(formData.returnQuantity);
    const qtyOk = Number.isFinite(qtyNum) && qtyNum >= 0; // permitir 0
    const hasResponsible = String(formData.responsibleDoc || '').trim() !== '';
    const hasItem = !!selectedAssignment;
    return qtyOk && hasResponsible && hasItem;
  }, [formData.returnQuantity, formData.responsibleDoc, selectedAssignment]);

  const addItem = () => {
    if (!canAdd) return;
    setItems(prev => [...prev, {
      assignmentId: formData.assignmentId || null,
      assignmentDbId: selectedAssignment?.assignmentDbId ?? null,
      responsibleDoc: (formData.responsibleDoc || '').trim() || null,
      responsibleName: selectedResponsibleName || ((formData.responsibleDoc || '').trim() ? String(formData.responsibleDoc).trim() : 'Sin responsable'),
      materialId: selectedAssignment?.materialId || null,
      materialName: selectedAssignment?.materialName || (selectedMaterial?.name || 'Sin material'),
      serial: String(selectedAssignment?.serial || '').trim(),
      brandName: String(selectedAssignment?.brandName || '').trim(),
      assignedQuantity: parseInt(selectedAssignment?.quantity) || 0,
      warehouseId: selectedAssignment?.warehouseId || selectedWarehouse || '',
      returnQuantity: parseInt(formData.returnQuantity) || 0,
      materialState: formData.materialState || '',
      observations: formData.observations || '',
      custodyVehicleId: Number(selectedAssignment?.custodyVehicleId || 0) || parseCustodyVehicleId(selectedAssignment?.observations),
    }]);
    setStatusMsg({ type: 'ok', text: 'Artículo agregado.' });
    setFormData({ ...formData, assignmentId: '', returnQuantity: 0, observations: '' });
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length > 0) {
      // Enviar lote completo para que el módulo padre maneje la iteración y la impresión agrupada
      const res = await onSave({ items });
      if (res && res.success) {
        setItems([]);
      }
      return;
    }
    onSave({
      ...formData,
      responsibleName: selectedResponsibleName || ((formData.responsibleDoc || '').trim() ? String(formData.responsibleDoc).trim() : 'Sin responsable'),
      serial: String(selectedAssignment?.serial || '').trim(),
      brandName: String(selectedAssignment?.brandName || '').trim(),
      materialId: selectedAssignment?.materialId || null,
      materialName: selectedAssignment?.materialName || '',
      assignedQuantity: parseInt(selectedAssignment?.quantity) || 0,
      warehouseId: selectedAssignment?.warehouseId || selectedWarehouse || '',
      assignmentDbId: selectedAssignment?.assignmentDbId ?? null
    });
  };

  

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Bodega</label>
          <select
            value={selectedWarehouse}
            onChange={(e) => {
              setSelectedWarehouse(e.target.value);
              setFormData({ ...formData, responsibleDoc: '', assignmentId: '' });
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Todas</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Responsable</label>
          <select
            value={formData.responsibleDoc}
            onChange={(e) => {
              setFormData({ ...formData, responsibleDoc: e.target.value, assignmentId: '' });
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Seleccione...</option>
            {responsibles.map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Material asignado</label>
          <select
            value={formData.assignmentId}
            onChange={(e) => {
              setFormData({ ...formData, assignmentId: e.target.value });
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Seleccione...</option>
            {personAssignments.map(a => (
              <option key={a.id} value={a.id}>
                {a.materialName} (Cant: {a.quantity}){String(a.serial || '').trim() ? ` - ${String(a.serial).trim()}` : ''}{String(a.brandName || '').trim() ? ` - ${String(a.brandName).trim()}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Cantidad a Devolver</label>
          <input
            type="number"
            value={formData.returnQuantity}
            onChange={(e) => setFormData({ ...formData, returnQuantity: e.target.value })}
            className="w-full border rounded px-3 py-2"
            min="0"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Cantidad asignada</label>
          <input
            type="text"
            value={selectedAssignment ? String(selectedAssignment.quantity) : ''}
            className="w-full border rounded px-3 py-2 bg-gray-50"
            readOnly
            placeholder="Seleccione material asignado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Marca</label>
          <input
            type="text"
            value={selectedAssignment ? String(selectedAssignment.brandName || '') : ''}
            className="w-full border rounded px-3 py-2 bg-gray-50"
            readOnly
            placeholder="—"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Serie</label>
          <input
            type="text"
            value={selectedAssignment ? String(selectedAssignment.serial || '') : ''}
            className="w-full border rounded px-3 py-2 bg-gray-50"
            readOnly
            placeholder="—"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Estado del Material</label>
          <select
            value={formData.materialState}
            onChange={(e) => setFormData({ ...formData, materialState: e.target.value })}
            className="w-full border rounded px-3 py-2"
          >
            <option>Bueno</option>
            <option>Regular</option>
            <option>Malo</option>
            <option>Dañado</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Stock actual del material</label>
          <input
            type="text"
            value={selectedMaterial && selectedMaterial.stockActual !== undefined ? String(selectedMaterial.stockActual) : ''}
            className="w-full border rounded px-3 py-2 bg-gray-50"
            readOnly
            placeholder="Seleccione material asignado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={selectedMaterial ? (selectedMaterial.code || '') : ''}
            className="w-full border rounded px-3 py-2 bg-gray-50"
            readOnly
            placeholder="Seleccione material asignado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Unidad</label>
          <input
            type="text"
            value={selectedMaterial ? (selectedMaterial.unit || '') : ''}
            className="w-full border rounded px-3 py-2 bg-gray-50"
            readOnly
            placeholder="Seleccione material asignado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Ubicación</label>
          <input
            type="text"
            value={selectedMaterial ? (selectedMaterial.location || '') : ''}
            className="w-full border rounded-md px-2 py-1.5 bg-gray-50"
            readOnly
            placeholder="Seleccione material asignado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Estado en Inventario</label>
          <input
            type="text"
            value={selectedMaterial ? (selectedMaterial.status || '') : ''}
            className="w-full border rounded-md px-2 py-1.5 bg-gray-50"
            readOnly
            placeholder="Seleccione material asignado"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Observaciones</label>
          <textarea
            value={formData.observations}
            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
            className="w-full border rounded px-3 py-2"
            rows="3"
            placeholder="Describa el estado del material, daños si los hay..."
          ></textarea>
        </div>
      </div>
      <div className="flex gap-2 mt-6">
        <button
          type="button"
          onClick={addItem}
          disabled={!canAdd}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
        >
          <Plus size={18} /> Agregar artículo
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 px-6 py-2 rounded flex items-center gap-2 hover:bg-gray-400"
        >
          <X size={18} /> Cancelar
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
        >
          <Save size={18} /> {items.length > 0 ? 'Guardar todo' : 'Guardar'}
        </button>
      </div>

      {items.length > 0 && (
        <div className="mt-4 border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Responsable</th>
                <th className="p-2 text-left">Material</th>
                <th className="p-2 text-center">Cant.</th>
                <th className="p-2 text-left">Estado</th>
                <th className="p-2 text-left">Observaciones</th>
                <th className="p-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-t">
                  <td className="p-2">{it.responsibleName || it.responsibleDoc}</td>
                  <td className="p-2">{it.materialName || it.materialId}</td>
                  <td className="p-2 text-center">{it.returnQuantity}</td>
                  <td className="p-2">{it.materialState}</td>
                  <td className="p-2">{it.observations || ''}</td>
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                    >
                      <Trash className="h-4 w-4" /> Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {statusMsg && (
        <p className={`text-sm mt-2 ${statusMsg.type==='error' ? 'text-red-600' : 'text-green-600'}`}>{statusMsg.text}</p>
      )}
    </form>
  );
};
